import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import RefreshIcon from "@mui/icons-material/Refresh";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import { API_URL } from "../../../../config";
import {
  useGetBanquetPricingMutation,
  useBulkSaveBanquetPricingMutation,
} from "../../../../Api/attoDeskApi";
import { useNotifier } from "../../../../Core/Notifier";
import { useStyles } from "../../../../theme/appStyles";
import { HALL_OPTIONS, DAY_TYPES, TIME_SLOTS } from "../constants";

// ── Types ──────────────────────────────────────────────────────────────────────
interface SlotRate {
  flatFee:        string;
  pricePerPerson: string;
}

// rates[hallKey][dayType][slotValue]
type RateMap = Record<string, Record<string, Record<string, SlotRate>>>;

// ── Hall display config ────────────────────────────────────────────────────────
const HALL_COLORS: Record<string, string> = {
  Nova:    "#1565c0",
  Titan:   "#2e7d32",
  Lunar:   "#e65100",
  Eclipse: "#6a1b9a",
};

const hallColor = (key: string): string => {
  const first = key.split(" ")[0];
  return HALL_COLORS[first] ?? "#1a2f47";
};

const hallIcon = (key: string): string => {
  const icons: Record<string, string> = { Nova: "N", Titan: "T", Lunar: "L", Eclipse: "E" };
  const first = key.split(" ")[0];
  return icons[first] ?? key.slice(0, 2).toUpperCase();
};

// ── Helpers ────────────────────────────────────────────────────────────────────
const emptySlot = (): SlotRate => ({ flatFee: "0", pricePerPerson: "0" });

const buildEmptyMap = (): RateMap => {
  const map: RateMap = {};
  for (const hall of HALL_OPTIONS) {
    map[hall.key] = {};
    for (const day of DAY_TYPES) {
      map[hall.key][day] = {};
      for (const slot of TIME_SLOTS) {
        map[hall.key][day][slot.value] = emptySlot();
      }
    }
  }
  return map;
};

const fmtPreview = (n: string | number): string => {
  const v = Number(n);
  if (!v) return "—";
  return "$" + v.toLocaleString("en-CA", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

// ── Component ──────────────────────────────────────────────────────────────────
const BasePricingTable = () => {
  const theme   = useTheme();
  const classes = useStyles();
  const { showMessage, showErrorMessage } = useNotifier();

  const [rates,      setRates]      = useState<RateMap>(buildEmptyMap());
  const [isDirty,    setIsDirty]    = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  const [getPricing]                        = useGetBanquetPricingMutation();
  const [bulkSave, { isLoading: isSaving }] = useBulkSaveBanquetPricingMutation();

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchPricing = useCallback(async () => {
    setIsFetching(true);
    try {
      const res = (await getPricing({}).unwrap()) as any;
      if (res.status && Array.isArray(res.data)) {
        const map = buildEmptyMap();
        for (const item of res.data) {
          const { hallKey: hk, dayType: dk, timeSlot: sk } = item;
          if (map[hk]?.[dk]?.[sk] !== undefined) {
            map[hk][dk][sk] = {
              flatFee:        String(item.flatFee        ?? 0),
              pricePerPerson: String(item.pricePerPerson ?? 0),
            };
          }
        }
        setRates(map);
        setIsDirty(false);
      }
    } catch (err: any) {
      showErrorMessage(err?.data?.message ?? "Failed to load pricing");
    } finally {
      setIsFetching(false);
    }
  }, [getPricing, showErrorMessage]);

  useEffect(() => { fetchPricing(); }, [fetchPricing]);

  // ── Update a single cell ───────────────────────────────────────────────────
  const update = (
    hallKey: string,
    dayType: string,
    slotVal: string,
    field: keyof SlotRate,
    value: string,
  ) => {
    setRates((prev) => ({
      ...prev,
      [hallKey]: {
        ...prev[hallKey],
        [dayType]: {
          ...prev[hallKey][dayType],
          [slotVal]: {
            ...prev[hallKey][dayType][slotVal],
            [field]: value,
          },
        },
      },
    }));
    setIsDirty(true);
  };

  // ── Generate Defaults ──────────────────────────────────────────────────────
  const [isGenerating, setIsGenerating] = useState(false);
  const handleGenerateDefaults = async () => {
    setIsGenerating(true);
    try {
      const res = await fetch(`${API_URL}/banquet/pricing/generate-defaults`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
        },
      });
      const data = await res.json();
      if (data.status) {
        showMessage(data.message ?? "Defaults generated");
        fetchPricing();
      } else {
        showErrorMessage(data.message ?? "Failed");
      }
    } catch {
      showErrorMessage("Failed to generate defaults");
    }
    setIsGenerating(false);
  };

  // ── Save All ───────────────────────────────────────────────────────────────
  const handleSave = async () => {
    const rules: any[] = [];
    for (const hall of HALL_OPTIONS) {
      for (const day of DAY_TYPES) {
        for (const slot of TIME_SLOTS) {
          const r = rates[hall.key]?.[day]?.[slot.value];
          if (!r) continue;
          rules.push({
            hallKey:        hall.key,
            hallCapacity:   hall.capacity,
            hallType:       hall.type,
            dayType:        day,
            timeSlot:       slot.value,
            flatFee:        Number(r.flatFee)        || 0,
            pricePerPerson: Number(r.pricePerPerson) || 0,
          });
        }
      }
    }
    try {
      const res = (await bulkSave({ rules }).unwrap()) as any;
      if (res.status) {
        showMessage(res.message ?? "Pricing saved");
        setIsDirty(false);
      } else {
        showErrorMessage(res.message ?? "Save failed");
      }
    } catch (err: any) {
      showErrorMessage(err?.data?.message ?? err?.message ?? "Save failed");
    }
  };

  const inputSx = {
    "& .MuiInputBase-root": { fontSize: 13, fontWeight: 600 },
    "& input": { py: "5px", px: "6px", fontSize: 12 },
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>

      {/* ── Toolbar ── */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Set flat fee + per-person rate for each hall, day type and time slot.
          {isDirty && (
            <Typography component="span" variant="body2" sx={{ color: "warning.main", ml: 1 }}>
              • unsaved changes
            </Typography>
          )}
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="Auto-fill empty (0.00) fields with smart defaults based on hall size">
            <span>
              <Button
                size="small"
                variant="outlined"
                onClick={handleGenerateDefaults}
                disabled={isGenerating || isFetching}
                startIcon={isGenerating ? <CircularProgress size={14} /> : <AutoFixHighIcon fontSize="small" />}
                sx={{ textTransform: "none", fontSize: 12, fontWeight: 600, borderColor: "#D6B98C", color: "#5A4633", "&:hover": { bgcolor: "#F5EDE4", borderColor: "#D6B98C" } }}
              >
                {isGenerating ? "Generating…" : "Generate Defaults"}
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Reload from server">
            <span>
              <Button
                size="small"
                variant="outlined"
                className={classes.addButton}
                onClick={fetchPricing}
                disabled={isFetching}
                sx={{ minWidth: 0, px: 1 }}
              >
                <RefreshIcon fontSize="small" />
              </Button>
            </span>
          </Tooltip>
          <Button
            size="small"
            variant="contained"
            className={classes.addButton}
            onClick={handleSave}
            disabled={isSaving || isFetching}
            startIcon={isSaving ? <CircularProgress size={14} color="inherit" /> : <SaveAltIcon />}
          >
            {isSaving ? "Saving…" : "Save All"}
          </Button>
        </Box>
      </Box>

      {isFetching && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      {/* ── Card grid: one section per hall ── */}
      {HALL_OPTIONS.map((hall) => {
        const color = hallColor(hall.key);
        const icon  = hallIcon(hall.key);
        return (
          <Box key={hall.key} sx={{ mb: 4 }}>
            {/* Hall group heading */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
              <Box
                sx={{
                  width: 36, height: 36,
                  borderRadius: "9px",
                  bgcolor: color,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: "#fff", fontSize: 13, fontWeight: 700, flexShrink: 0,
                }}
              >
                {icon}
              </Box>
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                  {hall.key}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {hall.capacity} pax · {hall.type}
                </Typography>
              </Box>
            </Box>

            {/* Day type cards */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(3,1fr)", lg: "repeat(5,1fr)" },
                gap: 1.5,
              }}
            >
              {DAY_TYPES.map((day) => (
                <Paper
                  key={day}
                  variant="outlined"
                  sx={{ overflow: "hidden", borderRadius: 2, transition: "box-shadow 0.15s", "&:hover": { boxShadow: theme.shadows[2] } }}
                >
                  {/* Card header */}
                  <Box
                    sx={{
                      px: 1.5, py: 1,
                      bgcolor: "action.hover",
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      display: "flex", alignItems: "center", gap: 1,
                    }}
                  >
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />
                    <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase" }}>
                      {day}
                    </Typography>
                  </Box>

                  {/* Slot rows */}
                  <Box sx={{ p: 1 }}>
                    {/* Column headers */}
                    <Box sx={{ display: "grid", gridTemplateColumns: "60px 1fr 1fr", gap: 0.5, mb: 0.5 }}>
                      <Box />
                      <Typography variant="caption" sx={{ fontSize: 10, color: "text.secondary", textAlign: "center" }}>Flat ($)</Typography>
                      <Typography variant="caption" sx={{ fontSize: 10, color: "text.secondary", textAlign: "center" }}>/person</Typography>
                    </Box>

                    {TIME_SLOTS.map((slot, sIdx) => {
                      const r = rates[hall.key]?.[day]?.[slot.value] ?? emptySlot();
                      return (
                        <React.Fragment key={slot.value}>
                          {sIdx > 0 && <Divider sx={{ my: 0.5 }} />}
                          <Box sx={{ display: "grid", gridTemplateColumns: "60px 1fr 1fr", gap: 0.5, alignItems: "center" }}>
                            <Typography variant="caption" sx={{ fontSize: 10, fontWeight: 600, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                              {slot.value === "FULL_DAY" ? "Full" : slot.value}
                            </Typography>
                            <TextField
                              size="small"
                              type="number"
                              value={r.flatFee}
                              onChange={(e) => update(hall.key, day, slot.value, "flatFee", e.target.value)}
                              inputProps={{ min: 0, step: 1, style: { textAlign: "right" } }}
                              sx={inputSx}
                            />
                            <TextField
                              size="small"
                              type="number"
                              value={r.pricePerPerson}
                              onChange={(e) => update(hall.key, day, slot.value, "pricePerPerson", e.target.value)}
                              inputProps={{ min: 0, step: 0.5, style: { textAlign: "right" } }}
                              sx={inputSx}
                            />
                          </Box>
                        </React.Fragment>
                      );
                    })}
                  </Box>
                </Paper>
              ))}
            </Box>
          </Box>
        );
      })}

      {/* ── Preview Table ── */}
      <Box sx={{ mt: 2, mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>Rate Preview</Typography>
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 1, overflowX: "auto" }}
        >
          <Table size="small" sx={{ minWidth: 700 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: "primary.main" }}>
                {["Hall", "Capacity", "Day Type", "AM — Flat", "AM — /pp", "PM — Flat", "PM — /pp", "Full — Flat", "Full — /pp"].map((h) => (
                  <TableCell key={h} sx={{ color: "primary.contrastText", fontWeight: 700, fontSize: 11, py: "8px", px: "10px", whiteSpace: "nowrap" }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {HALL_OPTIONS.flatMap((hall) =>
                DAY_TYPES.map((day, dIdx) => {
                  const am = rates[hall.key]?.[day]?.["AM"]       ?? emptySlot();
                  const pm = rates[hall.key]?.[day]?.["PM"]       ?? emptySlot();
                  const fd = rates[hall.key]?.[day]?.["FULL_DAY"] ?? emptySlot();
                  return (
                    <TableRow
                      key={`${hall.key}-${day}`}
                      sx={{ "&:last-child td": { border: 0 }, "&:hover td": { bgcolor: "action.hover" } }}
                    >
                      {dIdx === 0 ? (
                        <TableCell rowSpan={DAY_TYPES.length} sx={{ fontWeight: 700, fontSize: 12, px: "10px", verticalAlign: "top", pt: "10px", borderRight: `3px solid ${hallColor(hall.key)}` }}>
                          {hall.key}
                        </TableCell>
                      ) : null}
                      {dIdx === 0 ? (
                        <TableCell rowSpan={DAY_TYPES.length} sx={{ fontSize: 12, px: "10px", color: "text.secondary", verticalAlign: "top", pt: "10px" }}>
                          {hall.capacity}
                        </TableCell>
                      ) : null}
                      <TableCell sx={{ fontSize: 11, px: "10px", py: "6px" }}>
                        <Box component="span" sx={{ display: "inline-block", px: "8px", py: "2px", borderRadius: "20px", fontSize: 10, fontWeight: 600, bgcolor: "rgba(237,108,2,0.08)", color: "warning.dark", border: "1px solid rgba(237,108,2,0.3)" }}>
                          {day}
                        </Box>
                      </TableCell>
                      {(["AM", "PM", "FULL_DAY"] as const).flatMap((sk) => {
                        const r = sk === "AM" ? am : sk === "PM" ? pm : fd;
                        return [
                          <TableCell key={`${hall.key}-${day}-${sk}-f`} sx={{ fontSize: 13, fontWeight: 700, px: "10px", py: "6px" }}>
                            {fmtPreview(r.flatFee)}
                          </TableCell>,
                          <TableCell key={`${hall.key}-${day}-${sk}-p`} sx={{ fontSize: 12, px: "10px", py: "6px", color: "success.main", fontWeight: 600 }}>
                            {fmtPreview(r.pricePerPerson)}
                          </TableCell>,
                        ];
                      })}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {isDirty && (
        <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: "block" }}>
          You have unsaved changes. Click <strong>Save All</strong> to apply.
        </Typography>
      )}
    </Box>
  );
};

export default BasePricingTable;
